import { inject, Injectable } from '@angular/core';
import { interval, merge, Observable, of, Subject } from 'rxjs';
import { delay, startWith, switchMap, tap } from 'rxjs/operators';
import { ChatMessage, ChatThread } from '@models';
import { MeService } from '../me.service';
import { LogService } from '../log.service';
import { ChatService } from './chat.service';

const MOCK_PROVIDER_ID = 'mock-provider-1';

@Injectable({ providedIn: 'root' })
export class ChatMockService implements ChatService {
  private readonly meService = inject(MeService);
  private readonly logger = inject(LogService);
  private readonly currentUserId = this.meService.userId ?? MOCK_PROVIDER_ID;

  private readonly updates$ = new Subject<void>();

  private readonly unreadCounts = new Map<string, number>([
    ['mock-thread-1', 1],
    ['mock-thread-2', 0],
    ['mock-thread-3', 2],
    ['mock-thread-4', 0],
    ['mock-thread-5', 3],
  ]);

  private readonly threadList: ChatThread[] = [
    {
      threadId: 'mock-thread-1',
      counterparty: {
        userId: 'mock-patient-1',
        role: 'Patient',
        givenName: 'John',
        familyName: 'Smith',
        avatarUrl: null,
        email: 'john.smith@example.com',
      },
      lastMessagePreview: 'Is there anything I should watch out for?',
      lastMessageAt: new Date('2024-01-15T11:49:00'),
      unreadCount: 1,
    },
    {
      threadId: 'mock-thread-2',
      counterparty: {
        userId: 'mock-patient-2',
        role: 'Patient',
        givenName: 'Emily',
        familyName: 'Johnson',
        avatarUrl: null,
        email: 'emily.johnson@example.com',
      },
      lastMessagePreview: 'Thank you, I feel much better now.',
      lastMessageAt: new Date('2024-01-14T09:30:00'),
      unreadCount: 0,
    },
    {
      threadId: 'mock-thread-3',
      counterparty: {
        userId: 'mock-patient-3',
        role: 'Patient',
        givenName: 'Michael',
        familyName: 'Brown',
        avatarUrl: null,
        email: 'michael.brown@example.com',
      },
      lastMessagePreview: 'When is my next appointment?',
      lastMessageAt: new Date('2024-01-12T16:15:00'),
      unreadCount: 2,
    },
    {
      threadId: 'mock-thread-4',
      counterparty: {
        userId: 'mock-patient-4',
        role: 'Patient',
        givenName: 'Sarah',
        familyName: 'Davis',
        avatarUrl: null,
        email: 'sarah.davis@example.com',
      },
      lastMessagePreview: 'The new medication is working great!',
      lastMessageAt: new Date('2024-01-10T14:20:00'),
      unreadCount: 0,
    },
    {
      threadId: 'mock-thread-5',
      counterparty: {
        userId: 'mock-patient-5',
        role: 'Patient',
        givenName: 'Robert',
        familyName: 'Wilson',
        avatarUrl: null,
        email: 'robert.wilson@example.com',
      },
      lastMessagePreview: 'Should I continue taking the supplements?',
      lastMessageAt: new Date('2024-01-08T10:05:00'),
      unreadCount: 3,
    },
  ];

  private readonly threads = new Map<string, ChatMessage[]>([
    [
      'mock-thread-1',
      [
        {
          id: '1',
          senderId: 'mock-patient-1',
          senderName: 'John Smith',
          text: 'Hello Doctor, I wanted to check in about my recent test results.',
          sentAt: new Date('2024-01-15T11:40:00'),
        },
        {
          id: '2',
          senderId: this.currentUserId,
          senderName: 'Dr. Roberta Casey',
          text: 'Hi John! Your results look good overall. Your cholesterol levels have improved significantly.',
          sentAt: new Date('2024-01-15T11:44:00'),
        },
        {
          id: '3',
          senderId: 'mock-patient-1',
          senderName: 'John Smith',
          text: "Not great, actually. I've had a persistent cough and a bit of a sore throat for the past couple of days.",
          sentAt: new Date('2024-01-15T11:45:00'),
        },
        {
          id: '4',
          senderId: this.currentUserId,
          senderName: 'Dr. Roberta Casey',
          text: "Alright. Have you been in contact with anyone who's been unwell recently?",
          sentAt: new Date('2024-01-15T11:48:00'),
        },
        {
          id: '5',
          senderId: this.currentUserId,
          senderName: 'Dr. Roberta Casey',
          text: 'Is there anything I should watch out for?',
          sentAt: new Date('2024-01-15T11:49:00'),
        },
      ],
    ],
    [
      'mock-thread-2',
      [
        {
          id: '1',
          senderId: this.currentUserId,
          senderName: 'Dr. Roberta Casey',
          text: 'Hi Emily, how are you feeling after the treatment last week?',
          sentAt: new Date('2024-01-14T09:20:00'),
        },
        {
          id: '2',
          senderId: 'mock-patient-2',
          senderName: 'Emily Johnson',
          text: 'Much better! The new dosage seems to be working well.',
          sentAt: new Date('2024-01-14T09:25:00'),
        },
        {
          id: '3',
          senderId: 'mock-patient-2',
          senderName: 'Emily Johnson',
          text: 'Thank you, I feel much better now.',
          sentAt: new Date('2024-01-14T09:30:00'),
        },
      ],
    ],
    [
      'mock-thread-3',
      [
        {
          id: '1',
          senderId: 'mock-patient-3',
          senderName: 'Michael Brown',
          text: 'Doctor, I have a few questions about my treatment plan.',
          sentAt: new Date('2024-01-12T16:00:00'),
        },
        {
          id: '2',
          senderId: this.currentUserId,
          senderName: 'Dr. Roberta Casey',
          text: 'Of course Michael, what would you like to know?',
          sentAt: new Date('2024-01-12T16:08:00'),
        },
        {
          id: '3',
          senderId: 'mock-patient-3',
          senderName: 'Michael Brown',
          text: 'When is my next appointment?',
          sentAt: new Date('2024-01-12T16:15:00'),
        },
      ],
    ],
    [
      'mock-thread-4',
      [
        {
          id: '1',
          senderId: 'mock-patient-4',
          senderName: 'Sarah Davis',
          text: 'Hi Doctor! Just wanted to give you an update on how I am doing.',
          sentAt: new Date('2024-01-10T14:10:00'),
        },
        {
          id: '2',
          senderId: this.currentUserId,
          senderName: 'Dr. Roberta Casey',
          text: "Great to hear from you Sarah! How's the new medication treating you?",
          sentAt: new Date('2024-01-10T14:15:00'),
        },
        {
          id: '3',
          senderId: 'mock-patient-4',
          senderName: 'Sarah Davis',
          text: 'The new medication is working great!',
          sentAt: new Date('2024-01-10T14:20:00'),
        },
      ],
    ],
    [
      'mock-thread-5',
      [
        {
          id: '1',
          senderId: this.currentUserId,
          senderName: 'Dr. Roberta Casey',
          text: 'Robert, I reviewed your latest lab work. Everything looks stable.',
          sentAt: new Date('2024-01-08T09:50:00'),
        },
        {
          id: '2',
          senderId: 'mock-patient-5',
          senderName: 'Robert Wilson',
          text: 'That is a relief! I have been following the diet plan strictly.',
          sentAt: new Date('2024-01-08T10:00:00'),
        },
        {
          id: '3',
          senderId: 'mock-patient-5',
          senderName: 'Robert Wilson',
          text: 'Should I continue taking the supplements?',
          sentAt: new Date('2024-01-08T10:05:00'),
        },
      ],
    ],
    [
      'mock-patient-thread-1',
      [
        {
          id: '1',
          senderId: MOCK_PROVIDER_ID,
          senderName: 'Dr. Roberta Casey',
          text: 'Hello! How are you feeling today?',
          sentAt: new Date('2024-01-15T11:44:00'),
        },
        {
          id: '2',
          senderId: this.currentUserId,
          senderName: 'John Smith',
          text: "Not great, actually. I've had a persistent cough and a bit of a sore throat for the past couple of days.",
          sentAt: new Date('2024-01-15T11:45:00'),
        },
        {
          id: '3',
          senderId: MOCK_PROVIDER_ID,
          senderName: 'Dr. Roberta Casey',
          text: "Alright. Have you been in contact with anyone who's been unwell recently?",
          sentAt: new Date('2024-01-15T11:48:00'),
        },
      ],
    ],
  ]);

  getMessages(threadId: string): Observable<ChatMessage[]> {
    return of([...(this.threads.get(threadId) ?? [])]).pipe(delay(300));
  }

  sendMessage(threadId: string, text: string): Observable<void> {
    const messages = this.threads.get(threadId) ?? [];
    messages.push({
      id: String(Date.now()),
      senderId: this.currentUserId,
      senderName: 'Dr. Roberta Casey',
      text,
      sentAt: new Date(),
    });
    this.threads.set(threadId, messages);
    this.updates$.next();
    return of(void 0).pipe(delay(300));
  }

  watchMessages(threadId: string): Observable<ChatMessage[]> {
    return merge(this.updates$, interval(3_000)).pipe(
      startWith(null as null),
      tap(() => this.logger.log('ChatMockService poll fired')),
      switchMap(() => this.getMessages(threadId))
    );
  }

  watchThreads(): Observable<ChatThread[]> {
    return merge(this.updates$, interval(3_000)).pipe(
      startWith(null as null),
      tap(() => this.logger.log('ChatMockService threads poll fired')),
      switchMap(() => this.getThreads())
    );
  }

  markThreadAsRead(threadId: string): Observable<void> {
    this.unreadCounts.set(threadId, 0);
    const thread = this.threadList.find(t => t.threadId === threadId);
    if (thread) thread.unreadCount = 0;
    return of(void 0);
  }

  getThreads(): Observable<ChatThread[]> {
    return of(
      this.threadList.map(t => ({
        ...t,
        unreadCount: this.unreadCounts.get(t.threadId) ?? 0,
      }))
    ).pipe(delay(300));
  }
}
